import { motion } from "framer-motion";
import { Navbar, FinalCTA, Waveform, fadeUp, staggerContainer, btnMotion } from "../components/Shared";

const PROBLEMS = [
  "An EAP hotline employees don't know exists and rarely call",
  "One-off wellness webinars with no follow-through or behavior change",
  "Managers with no training for wellbeing or stress conversations",
  "Stress and burnout that stay invisible until someone quits",
  "Disconnected vendors for coaching, training, and wellness support — none of them talking to each other",
];

const MODULES = [
  { 
    name: "Confidential Wellness Coaching", 
    desc: "On-demand access to certified behavioral and wellness coaches via chat, call, or video — focused on resilience and stress regulation, not clinical therapy." 
  },
  { 
    name: "Stress & Burnout Prevention", 
    desc: "Evidence-based programs for regulating stress before it becomes burnout, personalized to individual patterns." 
  },
  { 
    name: "Communication & Behavior Coaching", 
    desc: "Practical coaching on everyday behaviors that shape team dynamics: feedback, conflict, focus, and follow-through." 
  },
  { 
    name: "Manager Enablement", 
    desc: "Training for managers to recognize strain early and hold supportive conversations without overstepping into clinical territory." 
  },
  { 
    name: "Personalized Plans", 
    desc: "Confidential assessments translate into a tailored support plan — not a generic content library nobody opens." 
  },
  { 
    name: "Aggregate Organizational Insights", 
    desc: "Leadership sees organizational trends and program engagement — never individual responses, session content, or recordings." 
  },
];

const STEPS = [
  { n: "01", title: "Your organization onboards", desc: "Set up takes days, not quarters. Employees get private, individual access from day one." },
  { n: "02", title: "Employees engage confidentially", desc: "Coaching, skill-building, and stress-management tools are available directly to each person." },
  { n: "03", title: "Support personalizes over time", desc: "Programs adapt as the person's needs change, instead of a static, one-size-fits-all library." },
  { n: "04", title: "Leadership sees the aggregate picture", desc: "Organization-wide trends in engagement and stress load — reported only in aggregate." },
  { n: "05", title: "Interventions scale proactively", desc: "Emerging trends trigger targeted resources before burnout spreads, keeping teams resilient and focused." },
  { n: "06", title: "Culture transforms measurably", desc: "Reduced turnover and sustained productivity turn well-being from a perk into a strategic advantage." },
];

const TRUST_POINTS = [
  "We do not record sessions. Individual conversations remain 100% confidential between the employee and coach.",
  "Leadership dashboards report aggregate trends only, never individual data or identities.",
  "Participation is strictly voluntary and never tied to performance reviews.",
];

export default function Home() {
  return (
    <main className="cw-home">
      <Navbar style={{width: '100%'}} />
      
      <section className="cw-section" style={{ paddingTop: 'var(--space-xl)' }}>
        <div className="cw-section__inner" style={{ textAlign: 'center' }}>
          <motion.div variants={staggerContainer(0.12)} initial="hidden" animate="visible">
            <motion.p className="cw-eyebrow" variants={fadeUp}>Corporate Wellbeing & Performance, All in One Platform</motion.p>
            <motion.h1 className="cw-hero__headline" variants={fadeUp} style={{ fontSize: '3.5rem', marginBottom: 'var(--space-md)' }}>
              One platform for every corporate wellbeing need.
            </motion.h1>
            <motion.p className="cw-hero__sub" variants={fadeUp} style={{ fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto var(--space-md)', opacity: 0.8 }}>
              Cognito Wellness brings confidential wellness coaching, stress management, and behavior coaching into a single system — built to support people and lift productivity.
            </motion.p>

            <motion.div className="cw-hero__actions" variants={fadeUp} style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: 'var(--space-lg)' }}>
              <motion.a 
                href="mailto:cs@cognitowellness.org?subject=Beta Pilot Inquiry: Cognito Wellness" 
                className="cw-btn cw-btn--primary" 
                {...btnMotion}
              >
                Start a 3-Month Pilot
              </motion.a>
              <motion.a href="#how" className="cw-btn cw-btn--ghost" {...btnMotion}>See how it works</motion.a>
            </motion.div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Waveform />
            </div>
          </motion.div>
        </div>
      </section>

      <motion.section className="cw-section cw-section--alt" variants={staggerContainer(0.06)} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
        <div className="cw-section__inner">
          <motion.p className="cw-eyebrow" variants={fadeUp}>The Problem</motion.p>
          <motion.h2 className="cw-section__title" variants={fadeUp} style={{ fontSize: '2.2rem', marginBottom: 'var(--space-md)' }}>
            Most companies buy corporate wellbeing in disconnected pieces.
          </motion.h2>
          <motion.ul className="cw-problem-list" variants={staggerContainer(0.06)} style={{ listStyle: 'none', display: 'grid', gap: 'var(--space-sm)' }}>
            {PROBLEMS.map((point) => (
              <motion.li key={point} className="cw-card" variants={fadeUp} style={{ borderLeft: '3px solid var(--cw-pink)' }}>
                {point}
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </motion.section>

      <motion.section className="cw-section" id="platform" variants={staggerContainer(0.08)} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
        <div className="cw-section__inner">
          <motion.p className="cw-eyebrow" variants={fadeUp}>The Platform</motion.p>
          <motion.h2 className="cw-section__title" variants={fadeUp} style={{ fontSize: '2.2rem', marginBottom: 'var(--space-lg)' }}>
            Everything a proactive wellness program needs, in one place.
          </motion.h2>
          <motion.div className="cw-module-grid" variants={staggerContainer(0.07)} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-md)' }}>
            {MODULES.map((mod) => (
              <motion.div className="cw-card" key={mod.name} variants={fadeUp}>
                <h3 className="cw-module-card__title" style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', marginBottom: 'var(--space-xs)' }}>{mod.name}</h3>
                <p className="cw-module-card__desc" style={{ fontSize: '0.95rem', opacity: 0.8 }}>{mod.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      <motion.section className="cw-section cw-section--alt" id="how" variants={staggerContainer(0.1)} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }}>
        <div className="cw-section__inner">
          <motion.p className="cw-eyebrow" variants={fadeUp}>How it works</motion.p>
          <motion.h2 className="cw-section__title" variants={fadeUp} style={{ fontSize: '2.2rem', marginBottom: 'var(--space-lg)' }}>From onboarding to insight.</motion.h2>
          <div className="cw-steps" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--space-md)' }}>
            {STEPS.map((step) => (
              <motion.div className="cw-card" key={step.n} variants={fadeUp}>
                <span className="cw-step__number" style={{ fontFamily: 'var(--font-mono)', color: 'var(--cw-pink)', fontSize: '0.85rem' }}>{step.n}</span>
                <h3 className="cw-step__title" style={{ fontFamily: 'var(--font-serif)', fontSize: '1.2rem', margin: 'var(--space-xs) 0' }}>{step.title}</h3>
                <p className="cw-step__desc" style={{ fontSize: '0.9rem', opacity: 0.8 }}>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section className="cw-section" id="trust" variants={staggerContainer(0.08)} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
        <div className="cw-section__inner" style={{ textAlign: 'center' }}>
          <motion.p className="cw-eyebrow" variants={fadeUp}>Confidentiality & Trust</motion.p>
          <motion.h2 className="cw-section__title" variants={fadeUp} style={{ fontSize: '2.2rem', marginBottom: 'var(--space-md)' }}>Built so employees can trust it — and use it.</motion.h2>
          <motion.ul className="cw-trust-list" variants={staggerContainer(0.06)} style={{ listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--space-md)', textAlign: 'left', marginTop: 'var(--space-lg)' }}>
            {TRUST_POINTS.map((point) => (
              <motion.li key={point} className="cw-card" variants={fadeUp} style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'flex-start' }}>
                <span style={{ color: 'var(--cw-teal)', fontSize: '1.2rem' }}>✓</span>
                <span style={{ fontSize: '0.95rem' }}>{point}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </motion.section>

      <FinalCTA />
    </main>
  );
}